import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { inject, injectable } from 'src/features/ioc';
import type {
    QueryCacheUserChangeEvent,
    QueryCacheUserChangeKind,
    QueryCacheUserService,
    QueryCacheUserServiceDataSource,
} from 'src/services/QueryCacheUserService';
import type { IsomorphicBindings } from 'src/types/bindings';

type QueryCacheUserQueryPredicate = (q: {
    options: { meta?: { shared?: boolean; entitlementInsensitive?: boolean } };
}) => boolean;

interface QueryCacheUserChangePolicy {
    reset: QueryCacheUserQueryPredicate;
    invalidate: QueryCacheUserQueryPredicate;
}

const isUserScopedQuery: QueryCacheUserQueryPredicate = (q) => q.options.meta?.shared !== true;
// A query opts out of the entitlements reset with `meta.entitlementInsensitive: true`
// when its result does not depend on the user's subscription tier (e.g. the global
// universe of searchable symbols). Identity changes still reset these — only
// entitlement-only changes (same user, new tier) skip them.
const isEntitlementSensitiveQuery: QueryCacheUserQueryPredicate = (q) =>
    isUserScopedQuery(q) && q.options.meta?.entitlementInsensitive !== true;
const neverInvalidateQuery: QueryCacheUserQueryPredicate = () => false;

const policyByChangeKind: Record<QueryCacheUserChangeKind, QueryCacheUserChangePolicy> = {
    identity: {
        reset: isUserScopedQuery,
        invalidate: neverInvalidateQuery,
    },
    entitlements: {
        reset: isEntitlementSensitiveQuery,
        invalidate: neverInvalidateQuery,
    },
};

@injectable()
export class QueryCacheUserServiceImpl implements QueryCacheUserService {
    readonly #dataSourceSubscription = new DisposableBox();

    constructor(@inject('QueryClient') private readonly queryClient: IsomorphicBindings['QueryClient']) {}

    initialize(dataSource: QueryCacheUserServiceDataSource): void {
        const unsubscribe = dataSource.subscribe((event) => {
            this.#handleUserChange(event);
        });
        this.#dataSourceSubscription.next({
            [Symbol.dispose]: unsubscribe,
        });
    }

    #handleUserChange(event: QueryCacheUserChangeEvent): void {
        const policy = policyByChangeKind[event.kind];

        if (policy.invalidate !== neverInvalidateQuery) {
            void this.queryClient.invalidateQueries({
                predicate: policy.invalidate,
            });
        }

        // resetQueries (not removeQueries): clears data synchronously AND refetches
        // active observers. removeQueries destroys the cache entry, leaving active observers
        // orphaned with stale data because they
        // only call setOptions — the TanStack path that rebinds observers to a fresh
        // Query — at init when options isn't a signal.
        void this.queryClient.resetQueries({
            predicate: policy.reset,
        });
    }

    [Symbol.dispose](): void {
        this.#dataSourceSubscription[Symbol.dispose]();
    }
}
